import { computed, observable } from 'mobx';
import { delay, noop } from '@src/utils/common-util';
import captchaHandlerDecorate from '@src/utils/captcha-deco-util';
import lockAsync from '@src/utils/lock-async';
import useUnmount from '@src/utils/hooks/use-unmount';
import BaseModalStore from '@src/components/modal-container/base-modal-store';
import TinyInputStore from '@src/components/tiny-input/store';
import Toast from '@src/components/toast';
import $$tracker from '@src/tracker';

const getErrorMessage = error => {
  const response = (error && error.serviceResponse) || error || {};
  return response.message || response.msg || '操作失败，请稍后重试';
};

class ReservationModalStore extends BaseModalStore {
  @observable mode = 'verification';
  @observable phone = '';
  @observable phoneDisplay = '';
  @observable account = '';
  @observable serverName = '新服名称';
  @observable friends = [];
  @observable isSubmitting = false;
  @observable isSendingCode = false;
  @observable countdown = 0;

  idNumberInputStore = new TinyInputStore();
  codeInputStore = new TinyInputStore();
  countdownToken = 0;
  onSuccess = noop;
  closeCallback = noop;

  constructor(options = {}) {
    super(options);
    Object.assign(this, options);
  }

  @computed
  get title() {
    return this.mode === 'verification' ? '账号验证弹窗' : this.mode === 'phoneBinding' ? '未绑定手机提示' : '预约成功';
  }

  showVerification(phone) {
    const value = String(phone || '');
    this.mode = 'verification';
    this.phone = value;
    this.phoneDisplay = /^(\d{3})\d{4}(\d{4})$/.test(value) ? value.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2') : value;
  }

  showPhoneBinding() {
    this.mode = 'phoneBinding';
  }

  showSuccess({ friends } = {}) {
    const config = (typeof window !== 'undefined' && window.__JXSJ_RESERVATION_CONFIG__) || {};
    this.mode = 'success';
    this.account = config.account || '';
    this.serverName = config.server_name || '新服名称';
    this.friends = Array.isArray(friends) ? friends.filter(Boolean).slice(0, 3) : [];
  }

  @captchaHandlerDecorate('zt')
  sendCodeHandler(params) {
    return this.$ztService.getPhoneCode(params);
  }

  @lockAsync()
  async requestCode() {
    this.isSendingCode = true;
    try {
      const result = await this.sendCodeHandler({});
      if (result && result.$state === 0) {
        return false;
      }
      this.startCountdown(60);
      return true;
    } catch (error) {
      Toast.fail(getErrorMessage(error), 1);
      return false;
    } finally {
      this.isSendingCode = false;
    }
  }

  async startCountdown(seconds) {
    const token = ++this.countdownToken;
    this.countdown = seconds;
    while (this.countdown > 0 && token === this.countdownToken) {
      await delay(1000);
      if (token === this.countdownToken) {
        this.countdown -= 1;
      }
    }
  }

  cancelCountdown() {
    this.countdownToken += 1;
    this.countdown = 0;
    this.isSendingCode = false;
  }

  async submitReservation() {
    $$tracker.clickIdentityValidate();
    const idNumber = this.idNumberInputStore.trimmedValue;
    const code = this.codeInputStore.trimmedValue;
    if (this.isSubmitting) {
      return false;
    }
    if (!idNumber || !code) {
      Toast.fail(!idNumber ? '请输入身份证号码' : '请输入验证码', 1);
      return false;
    }
    this.isSubmitting = true;
    try {
      const result = await this.$ztService.activate(code, idNumber);
      window.__JXSJ_ACTIVATE_RESULT__ = result;
      await this.onSuccess(result);
      return true;
    } catch (error) {
      Toast.fail(getErrorMessage(error), 1);
      return false;
    } finally {
      this.isSubmitting = false;
    }
  }

  onClickClose = () => {
    const shouldReturnToGuest = this.mode === 'success';
    this.isOpen = false;
    this.isActive = false;
    this.cancelCountdown();
    this.clearStyle();
    shouldReturnToGuest && this.closeCallback();
  };

  use() {
    super.use();
    useUnmount(() => this.cancelCountdown());
  }
}

export default ReservationModalStore;
